import {useEffect, useState} from "react"
import axios from "axios"
import {Alert, Badge, Button, Card, Spinner, Table} from "react-bootstrap"

import {useAuth} from "../../context/AuthProvider"
import {useTranslation} from "react-i18next"

type ScheduledJob = {
    name: string
    description: string
    cronExpression?: string
    intervalMs?: number
    enabled: boolean
    running: boolean
}

const formatSchedule = (job: ScheduledJob, t: (key: string, options?: any) => string): string => {
    if (job.cronExpression) return `Cron: ${job.cronExpression}`
    if (job.intervalMs) return t("scheduler.everySeconds", {count: job.intervalMs / 1000})
    return t("scheduler.noSchedule")
}

export default function SchedulerAdminComponent() {
    const {t} = useTranslation("admin")
    const auth = useAuth()
    const isAdmin = auth.hasGate("admin")
    const [jobs, setJobs] = useState<ScheduledJob[]>([])
    const [loading, setLoading] = useState(true)
    const [runningJob, setRunningJob] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [success, setSuccess] = useState<string | null>(null)

    const load = async () => {
        if (!isAdmin) {
            setLoading(false)
            return
        }

        try {
            const response = await axios.get<ScheduledJob[]>(
                "/api/schedule",
                auth.axiosAuthHeader()
            )
            setJobs(response.data)
            setError(null)
        } catch (error: unknown) {
            setError(axios.isAxiosError(error)
                ? error.response?.data?.msg ?? t("scheduler.loadError")
                : t("scheduler.loadError"))
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        load()
    }, [isAdmin])

    const run = async (job: ScheduledJob) => {
        if (!window.confirm(t("scheduler.confirm", {name: job.name}))) return

        setRunningJob(job.name)
        setError(null)
        setSuccess(null)

        try {
            await axios.post(
                `/api/schedule/${encodeURIComponent(job.name)}/run`,
                {},
                auth.axiosAuthHeader()
            )
            setSuccess(t("scheduler.success", {name: job.name}))
            await load()
        } catch (error: unknown) {
            setError(axios.isAxiosError(error)
                ? error.response?.data?.msg ?? t("scheduler.runError", {name: job.name})
                : t("scheduler.runError", {name: job.name}))
        } finally {
            setRunningJob(null)
        }
    }

    if (!isAdmin) {
        return <div className="component">
            <Alert variant="danger">{t("adminOnly")}</Alert>
        </div>
    }

    return <div className="component">
        <Card>
            <Card.Header>
                <h3 className="mb-0"><i className="bi bi-clock-history me-2"/>Scheduler</h3>
            </Card.Header>
            <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                {loading ? (
                    <div className="d-flex align-items-center gap-2">
                        <Spinner animation="border" size="sm"/> {t("scheduler.loading")}
                    </div>
                ) : (
                    <Table responsive hover className="mb-0">
                        <thead><tr>
                            <th>{t("scheduler.columns.name")}</th><th>{t("scheduler.columns.description")}</th><th>{t("scheduler.columns.schedule")}</th>
                            <th>{t("scheduler.columns.status")}</th><th className="text-end">{t("scheduler.columns.action")}</th>
                        </tr></thead>
                        <tbody>
                        {jobs.map(job => {
                            const running = runningJob === job.name || job.running
                            return <tr key={job.name}>
                                <td><code>{job.name}</code></td>
                                <td>{job.description}</td>
                                <td>{formatSchedule(job, t)}</td>
                                <td>
                                    <Badge bg={job.enabled ? "success" : "secondary"}>
                                        {t(job.enabled ? "scheduler.active" : "scheduler.disabled")}
                                    </Badge>
                                </td>
                                <td className="text-end">
                                    <Button size="sm" disabled={running} onClick={() => run(job)}>
                                        {running && <Spinner animation="border" size="sm" className="me-2"/>}
                                        {t(running ? "scheduler.running" : "scheduler.runNow")}
                                    </Button>
                                </td>
                            </tr>
                        })}
                        </tbody>
                    </Table>
                )}
            </Card.Body>
        </Card>
    </div>
}
